import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface CalendarBlock {
  id: string;
  ownerUserId: string;
  startUtc: string;
  endUtc: string;
  label?: string | null;
  publicSummary: string;
  isAllDay: boolean;
  recurrenceRule?: string | null;
  graphEventId?: string | null;
  oooBroadcastEventId?: string | null;
  linkedToExistingOutlookEvent: boolean;
}

export interface OutlookCalendarEvent {
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  graphEventId: string;
  sourceId: string;
  subject?: string | null;
}

export interface CalendarFeedToken {
  id: string;
  ownerUserId: string;
  token: string;
  feedName?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

const BLOCKS_KEY = ["calendar", "blocks"];
const FEEDS_KEY = ["calendar", "feeds"];
const OUTLOOK_EVENTS_KEY = ["calendar", "outlook-events"];

export function useCalendarBlocks() {
  return useQuery({
    queryKey: BLOCKS_KEY,
    queryFn: async (): Promise<CalendarBlock[]> => {
      const r = await api().get<CalendarBlock[]>("/api/calendar/blocks");
      return r.data;
    },
  });
}

export function useCreateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<CalendarBlock>) => {
      const r = await api().post<CalendarBlock>("/api/calendar/blocks", input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BLOCKS_KEY }),
  });
}

export function useUpdateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CalendarBlock> & { id: string }) => {
      const r = await api().put<CalendarBlock>(`/api/calendar/blocks/${id}`, input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BLOCKS_KEY }),
  });
}

export function useDeleteBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/calendar/blocks/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BLOCKS_KEY });
      qc.invalidateQueries({ queryKey: OUTLOOK_EVENTS_KEY });
    },
  });
}

export function useOutlookEvents() {
  return useQuery({
    queryKey: OUTLOOK_EVENTS_KEY,
    queryFn: async (): Promise<OutlookCalendarEvent[]> => {
      const r = await api().get<OutlookCalendarEvent[]>("/api/calendar/outlook-events");
      return r.data;
    },
    refetchInterval: 60_000,
  });
}

export function useSyncOutlookToFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (graphEventId: string) => {
      const r = await api().post<CalendarBlock>("/api/calendar/blocks/from-outlook", { graphEventId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BLOCKS_KEY });
      qc.invalidateQueries({ queryKey: OUTLOOK_EVENTS_KEY });
    },
  });
}

export function useCalendarFeeds() {
  return useQuery({
    queryKey: FEEDS_KEY,
    queryFn: async (): Promise<CalendarFeedToken[]> => {
      const r = await api().get<CalendarFeedToken[]>("/api/calendar/feeds");
      return r.data;
    },
  });
}

export function useCreateFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (feedName?: string) => {
      const r = await api().post<CalendarFeedToken>("/api/calendar/feeds", { feedName });
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FEEDS_KEY }),
  });
}

export function useRevokeFeed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/calendar/feeds/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: FEEDS_KEY }),
  });
}

export function useBroadcastOoo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blockId, attendeeEmails }: { blockId: string; attendeeEmails: string[] }) => {
      const r = await api().post<{ oooEventId: string }>(
        `/api/calendar/blocks/${blockId}/broadcast-ooo`,
        { attendeeEmails },
      );
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BLOCKS_KEY }),
  });
}
